import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';

import {
  criarPergunta,
  criarPerguntaBase,
  excluirPergunta,
  excluirPerguntaBase,
  type PerguntaInput,
  type PerguntaQualificacao,
} from '@/api/secretario';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

import type { EscopoEditor } from './DocumentosRequeridosEditor';

interface QuestionarioEditorProps {
  escopo: EscopoEditor;
  perguntas: PerguntaQualificacao[];
}

/** Questionário de qualificação que o secretário aplica (§5/§7). Itens com
 * condição só são perguntados se a condição se aplicar ao caso. */
export function QuestionarioEditor({ escopo, perguntas }: QuestionarioEditorProps) {
  const queryClient = useQueryClient();
  const { showError } = useNotification();
  const [novoTexto, setNovoTexto] = useState('');
  const [novaCondicao, setNovaCondicao] = useState('');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.secretario.config });

  const proximaOrdem = perguntas.reduce((max, p) => Math.max(max, p.ordem), -1) + 1;
  const criar = useMutation({
    mutationFn: (payload: PerguntaInput) =>
      escopo.tipo === 'area' ? criarPergunta(escopo.areaId, payload) : criarPerguntaBase(payload),
    onSuccess: () => {
      setNovoTexto('');
      setNovaCondicao('');
      invalidate();
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  const excluir = useMutation({
    mutationFn: (id: string) =>
      escopo.tipo === 'area' ? excluirPergunta(escopo.areaId, id) : excluirPerguntaBase(id),
    onSuccess: invalidate,
    onError: (error) => notifyMutationError(error, showError),
  });

  const podeAdicionar = !!novoTexto.trim() && !criar.isPending;
  const adicionar = () => {
    if (podeAdicionar) {
      criar.mutate({ texto: novoTexto.trim(), condicao: novaCondicao.trim() || null, ordem: proximaOrdem });
    }
  };

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium">Perguntas de qualificação</h4>
      {perguntas.length === 0 && <p className="text-xs text-muted-foreground">Nenhuma pergunta configurada.</p>}
      <ul className="space-y-1.5">
        {perguntas.map((pergunta) => (
          <li key={pergunta.id} className="flex items-start justify-between gap-2 rounded-md border px-3 py-1.5">
            <span className="flex flex-col gap-0.5 text-sm">
              {pergunta.texto}
              {pergunta.condicao && (
                <span className="text-xs text-muted-foreground">Só se {pergunta.condicao}</span>
              )}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => excluir.mutate(pergunta.id)}
              disabled={excluir.isPending}
              aria-label="Remover pergunta"
            >
              <Trash2 size={16} />
            </Button>
          </li>
        ))}
      </ul>
      <div className="space-y-2">
        <div className="flex gap-2">
          <Input
            value={novoTexto}
            onChange={(e) => setNovoTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                adicionar();
              }
            }}
            placeholder="Ex.: Há quanto tempo o problema acontece?"
            maxLength={500}
          />
          <Button type="button" variant="outline" onClick={adicionar} disabled={!podeAdicionar}>
            <Plus size={16} className="mr-1" />
            Adicionar
          </Button>
        </div>
        <Input
          value={novaCondicao}
          onChange={(e) => setNovaCondicao(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              adicionar();
            }
          }}
          placeholder="Condição (opcional) — ex.: se houver imóvel envolvido"
          maxLength={300}
        />
      </div>
    </div>
  );
}
